import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveImage, VimeoPlayer, VideoPlayer } from 'components';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { selectIsRobot } from 'store/index';

const Media = forwardRef(function Media(
  {
    media,
    widths,
    heights,
    preload,
    cdnProps,
    dataNotLazy,
    imgProps,
    cover,
    controls,
    muted,
    className,
    onAutoPlayStarted,
    onPayerReady,
    ...props
  },
  ref,
) {
  const isRobot = useSelector(selectIsRobot);

  const vimeoPlayer = useRef();
  const videoPlayer = useRef();

  useImperativeHandle(
    ref,
    () => {
      return {
        openFullscreen() {
          if (media.vimeoId) {
            vimeoPlayer?.current?.openFullscreen();
          } else if (media.videoFromGallery) {
            videoPlayer?.current?.openFullscreen();
          }
        },
        play() {
          if (media.vimeoId) {
            vimeoPlayer?.current?.play();
          } else if (media.videoFromGallery) {
            videoPlayer?.current?.play();
          }
        },
        pause() {
          if (media.vimeoId) {
            vimeoPlayer?.current?.pause();
          } else if (media.videoFromGallery) {
            videoPlayer?.current?.pause();
          }
        },
        getDuration() {
          if (media.vimeoId) {
            return vimeoPlayer?.current?.getDuration();
          }
        },
        getCurrentTime() {
          if (media.vimeoId) {
            return vimeoPlayer?.current?.getCurrentTime();
          }
        },
      };
    },
    [media],
  );

  if (!media) {
    return null;
  }

  return (
    <div className={classNames('w-full h-full relative overflow-hidden', className)} {...props}>
      {media.coverImage && (
        <ResponsiveImage
          image={media.coverImage}
          widths={widths}
          heights={heights}
          preload={preload}
          cdnProps={cdnProps}
          dataNotLazy={dataNotLazy}
          imgProps={imgProps}
          className="object-cover"
          hasPriority
        />
      )}
      {!isRobot &&
        (media.videoFromGallery || media.vimeoId || media.youtubeId || media.mobileVimeoId) &&
        (media.vimeoId || media.mobileVimeoId ? (
          <VimeoPlayer
            ref={vimeoPlayer}
            media={media}
            cover={cover}
            controls={controls}
            onAutoPlayStarted={onAutoPlayStarted}
            muted={muted}
            onPayerReady={onPayerReady}
          />
        ) : media.videoFromGallery ? (
          <VideoPlayer
            ref={videoPlayer}
            media={media}
            cover={cover}
            controls={controls}
            onAutoPlayStarted={onAutoPlayStarted}
            muted={muted}
          />
        ) : null)}
    </div>
  );
});

Media.propTypes = {
  media: PropTypes.object.isRequired,
  widths: PropTypes.object,
  heights: PropTypes.object,
  preload: PropTypes.bool,
  cdnProps: PropTypes.object,
  dataNotLazy: PropTypes.bool,
  imgProps: PropTypes.object,
  cover: PropTypes.bool,
  controls: PropTypes.bool,
};

export default Media;
